// src/components/TripManager.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Calendar, MapPin } from 'lucide-react';
import apiService from '../services/apiService';
import { errorLog, debugLog, warningLog } from '../config/featureFlags';

const TripManager = ({ onTripSelect, selectedTripId }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTripForm, setShowTripForm] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [tripForm, setTripForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    notes: ''
  });

  // Cargar viajes al montar el componente
  useEffect(() => {
    loadTrips();
  }, []);

  const loadTrips = async () => {
    setLoading(true);
    try {
      const data = await apiService.trip.getAll();
      debugLog('🔍 DEBUG: Datos de viajes recibidos:', data, 'Tipo:', typeof data, 'Es array:', Array.isArray(data));
      
      // ✅ VALIDACIÓN: Asegurar que data sea un array
      if (Array.isArray(data)) {
        setTrips(data);
      } else if (data && Array.isArray(data.trips)) {
        setTrips(data.trips);
      } else if (data && Array.isArray(data.data)) {
        setTrips(data.data);
      } else {
        warningLog('⚠️ Formato de datos inesperado para viajes:', data);
        setTrips([]);
      }
    } catch (err) {
      errorLog('❌ Error cargando viajes:', err);
      setError('Error al cargar viajes: ' + err.message);
      setTrips([]); // ✅ Asegurar que trips sea siempre un array
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    if (!tripForm.name.trim()) {
      setError('El nombre del viaje es obligatorio');
      return;
    }

    setLoading(true);
    try {
      const newTrip = await apiService.trip.create(tripForm);
      setTrips([...trips, newTrip]);
      setShowTripForm(false);
      setTripForm({ name: '', startDate: '', endDate: '', notes: '' });
      setError('');
    } catch (err) {
      setError('Error al crear viaje: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTrip = async (e) => {
    e.preventDefault();
    if (!tripForm.name.trim()) {
      setError('El nombre del viaje es obligatorio');
      return;
    }

    setLoading(true);
    try {
      const updatedTrip = await apiService.trip.update(editingTrip.id, tripForm);
      setTrips(trips.map(trip => trip.id === editingTrip.id ? updatedTrip : trip));
      setEditingTrip(null);
      setTripForm({ name: '', startDate: '', endDate: '', notes: '' });
      setError('');
    } catch (err) {
      setError('Error al actualizar viaje: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTrip = (trip) => {
    setEditingTrip(trip);
    setTripForm({
      name: trip.name,
      startDate: trip.startDate || '',
      endDate: trip.endDate || '',
      notes: trip.notes || ''
    });
    setShowTripForm(true);
  };

  const handleDeleteTrip = async (tripId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este viaje?')) {
      return;
    }

    setLoading(true);
    try {
      await apiService.trip.delete(tripId);
      setTrips(trips.filter(trip => trip.id !== tripId));
      if (selectedTripId === tripId) {
        onTripSelect(null);
      }
    } catch (err) {
      setError('Error al eliminar viaje: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'planning': return 'Planificando';
      case 'active': return 'Activo';
      case 'completed': return 'Completado';
      default: return 'Desconocido';
    }
  };

  return (
    <div className="py-6 w-full px-2 sm:px-4 md:px-6 lg:px-8 xl:px-12 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <MapPin className="text-blue-600" size={28} />
            Gestión de Viajes
          </h2>
          <p className="text-gray-600">Administra tus viajes a Orlando</p>
        </div>
        <button
          onClick={() => {
            setShowTripForm(true);
            setEditingTrip(null);
            setTripForm({ name: '', startDate: '', endDate: '', notes: '' });
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Nuevo Viaje
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Trip Form Modal */}
      {showTripForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingTrip ? 'Editar Viaje' : 'Crear Nuevo Viaje'}
            </h3>
            
            <form onSubmit={editingTrip ? handleUpdateTrip : handleCreateTrip} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Viaje *
                </label>
                <input
                  type="text"
                  value={tripForm.name}
                  onChange={(e) => setTripForm({...tripForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Viaje Enero 2025 - Orlando"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Salida
                  </label>
                  <input
                    type="date"
                    value={tripForm.startDate}
                    onChange={(e) => setTripForm({...tripForm, startDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Regreso
                  </label>
                  <input
                    type="date"
                    value={tripForm.endDate}
                    onChange={(e) => setTripForm({...tripForm, endDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={tripForm.notes}
                  onChange={(e) => setTripForm({...tripForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Notas adicionales sobre el viaje..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowTripForm(false);
                    setEditingTrip(null);
                    setTripForm({ name: '', startDate: '', endDate: '', notes: '' });
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : (editingTrip ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Trips List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(trips) ? (
          trips.map((trip) => (
          <div
            key={trip.id}
            className={`border rounded-lg p-6 cursor-pointer transition-all hover:shadow-md ${
              selectedTripId === trip.id 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            onClick={() => onTripSelect(trip)}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-800 mb-2">
                  {trip.name}
                </h3>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trip.status)}`}>
                  {getStatusText(trip.status)}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditTrip(trip);
                  }}
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                  title="Editar viaje"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteTrip(trip.id);
                  }}
                  className="text-gray-400 hover:text-red-600 transition-colors"
                  title="Eliminar viaje"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {trip.startDate && (() => {
                const startDate = new Date(trip.startDate);
                return !isNaN(startDate.getTime()) ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={14} />
                    <span>Salida: {startDate.toLocaleDateString('es-CO')}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar size={14} />
                    <span>Fecha no disponible</span>
                  </div>
                );
              })()}
              {trip.endDate && (() => {
                const endDate = new Date(trip.endDate);
                return !isNaN(endDate.getTime()) ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={14} />
                    <span>Regreso: {endDate.toLocaleDateString('es-CO')}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar size={14} />
                    <span>Fecha no disponible</span>
                  </div>
                );
              })()}
              {!trip.startDate && !trip.endDate && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar size={14} />
                  <span>Fecha no disponible</span>
                </div>
              )}
              {trip.notes && (
                <p className="text-sm text-gray-600 mt-2">
                  {trip.notes}
                </p>
              )}
            </div>

            {selectedTripId === trip.id && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="text-sm text-blue-600 font-medium">
                  ✓ Viaje seleccionado
                </p>
              </div>
            )}
          </div>
          ))
        ) : (
          <div className="col-span-full text-center py-8">
            <p className="text-gray-500">Error cargando viajes. Los datos no están en el formato esperado.</p>
          </div>
        )}
      </div>

      {trips.length === 0 && !loading && (
        <div className="text-center py-12">
          <MapPin size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No hay viajes creados</h3>
          <p className="text-gray-500 mb-4">Crea tu primer viaje para comenzar a organizar tus en vivos</p>
          <button
            onClick={() => {
              setShowTripForm(true);
              setEditingTrip(null);
              setTripForm({ name: '', startDate: '', endDate: '', notes: '' });
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors"
          >
            <Plus size={20} />
            Crear Primer Viaje
          </button>
        </div>
      )}

      {loading && trips.length === 0 && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Cargando viajes...</p>
        </div>
      )}
    </div>
  );
};

export default TripManager;
